import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';

type OfferState = 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'COUNTERED' | 'EXPIRED' | 'WITHDRAWN' | 'CANCELLED';

const CANCEL_REASON_LABEL: Record<string, string> = { ITEM_SOLD: 'Item sold' };

interface Offer {
  id: string;
  listing: { id: string; title: string; brand: string; priceGBP: number; imageUrl?: string };
  amountGBP: number;
  counterAmountGBP?: number;
  state: OfferState;
  cancelReason?: string;
  fromMe: boolean;
  counterparty: string;
  expiresAt: string;
  createdAt: string;
}

export default function Offers() {
  const [tab, setTab] = useState<'incoming' | 'outgoing'>('incoming');
  const qc = useQueryClient();

  const { data: offers = [], isLoading } = useQuery<Offer[]>({
    queryKey: ['offers', tab],
    queryFn: async () => (await api.get('/offers', { params: { side: tab } })).data,
  });

  const respond = useMutation({
    mutationFn: async (vars: { id: string; action: 'accept'|'decline'|'counter'|'withdraw'; counterGBP?: number }) =>
      (await api.post(`/offers/${vars.id}/${vars.action}`, { counterGBP: vars.counterGBP })).data,
    onSuccess: () => { toast.success('Offer updated.'); qc.invalidateQueries({ queryKey: ['offers'] }); },
    onError:   () => toast.error('Could not update offer.'),
  });

  return (
    <div>
      <div className="stamp text-gold-dark">Negotiations</div>
      <h1 className="display text-4xl mt-2">Offers</h1>
      <p className="text-sm text-mute mt-2 max-w-[60ch]">
        Buyers can offer below ticket price; sellers can accept, decline or counter.
        All accepted offers go straight to escrow checkout.
      </p>

      {/* Tab strip */}
      <div className="mt-7 inline-flex bg-cream-deep rounded-full p-1">
        {(['incoming', 'outgoing'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-5 py-2 text-sm rounded-full transition capitalize ${
              tab === t ? 'bg-ink text-cream shadow-sm' : 'text-mute hover:text-ink'
            }`}
          >
            {t} {t === 'incoming' ? '· on your listings' : '· on items you want'}
          </button>
        ))}
      </div>

      <div className="mt-8 space-y-4">
        {isLoading && (
          <div className="card p-6 skeleton h-24 rounded-luxe" />
        )}
        {!isLoading && offers.length === 0 && (
          <div className="card p-10 text-center">
            <div className="display text-2xl text-mute">No {tab} offers yet</div>
            <p className="text-sm text-mute mt-2">
              {tab === 'incoming'
                ? 'When someone offers below your ticket price, it lands here.'
                : 'Found something you love? Try “Make an offer” on the product page.'}
            </p>
            <Link to="/c/all" className="btn btn-outline mt-5">Browse pieces</Link>
          </div>
        )}
        {offers.map((o) => (
          <OfferRow key={o.id} offer={o} onAction={(a, c) => respond.mutate({ id: o.id, action: a, counterGBP: c })} />
        ))}
      </div>
    </div>
  );
}

function OfferRow({
  offer, onAction,
}: { offer: Offer; onAction: (a: 'accept'|'decline'|'counter'|'withdraw', counter?: number) => void }) {
  const [counter, setCounter] = useState('');
  const [showCounter, setShowCounter] = useState(false);
  const pct = Math.round((offer.amountGBP / offer.listing.priceGBP) * 100);

  return (
    <div className="card p-5 grid md:grid-cols-12 gap-5 items-center">
      <Link to={`/p/${offer.listing.id}`} className="md:col-span-5 flex items-center gap-4 group">
        <div
          className="w-20 h-20 notched-sm bg-gradient-to-br from-ink-700 to-ink shrink-0"
          style={offer.listing.imageUrl ? { backgroundImage: `url(${offer.listing.imageUrl})`, backgroundSize: 'cover' } : undefined}
        />
        <div className="min-w-0">
          <div className="stamp text-mute">{offer.listing.brand}</div>
          <div className="display text-xl truncate group-hover:text-gold-dark">{offer.listing.title}</div>
          <div className="text-xs text-mute mt-0.5">Listed at £{offer.listing.priceGBP.toFixed(0)} · {offer.fromMe ? 'You' : offer.counterparty}</div>
        </div>
      </Link>

      <div className="md:col-span-3 text-right md:text-left">
        <div className="stamp text-gold-dark">{offer.fromMe ? 'Your offer' : 'They offered'}</div>
        <div className="display text-3xl text-ink">£{offer.amountGBP.toFixed(0)}</div>
        <div className="text-xs text-mute mt-0.5">{pct}% of asking</div>
        {offer.counterAmountGBP && (
          <div className="mt-1 stamp text-clay-dark">Countered £{offer.counterAmountGBP.toFixed(0)}</div>
        )}
      </div>

      <div className="md:col-span-2">
        <StatePill state={offer.state} />
        {offer.state === 'CANCELLED' && offer.cancelReason ? (
          <div className="stamp text-mute mt-2">{CANCEL_REASON_LABEL[offer.cancelReason] ?? offer.cancelReason}</div>
        ) : (
          <div className="stamp text-mute mt-2">Expires {new Date(offer.expiresAt).toLocaleDateString()}</div>
        )}
      </div>

      <div className="md:col-span-2 flex flex-col gap-1.5">
        {offer.fromMe ? (
          offer.state === 'PENDING' && (
            <button onClick={() => onAction('withdraw')} className="btn btn-sm btn-outline">Withdraw</button>
          )
        ) : (
          offer.state === 'PENDING' && (
            <>
              <button onClick={() => onAction('accept')} className="btn btn-sm btn-primary">Accept → £{offer.amountGBP.toFixed(0)}</button>
              <div className="grid grid-cols-2 gap-1.5">
                <button onClick={() => setShowCounter(!showCounter)} className="btn btn-sm btn-outline">Counter</button>
                <button onClick={() => onAction('decline')} className="btn btn-sm btn-ghost border border-warmline">Decline</button>
              </div>
              {showCounter && (
                <div className="flex gap-1.5">
                  <input
                    type="number"
                    value={counter}
                    onChange={(e) => setCounter(e.target.value)}
                    placeholder={`£${Math.floor((offer.amountGBP + offer.listing.priceGBP) / 2)}`}
                    className="input text-sm py-1.5"
                  />
                  <button
                    disabled={!counter}
                    onClick={() => { onAction('counter', Number(counter)); setShowCounter(false); }}
                    className="btn btn-sm btn-clay"
                  >Send</button>
                </div>
              )}
            </>
          )
        )}
      </div>
    </div>
  );
}

function StatePill({ state }: { state: OfferState }) {
  const map: Record<OfferState, string> = {
    PENDING:    'pill-warn',
    ACCEPTED:   'pill-ok',
    DECLINED:   'pill-bad',
    COUNTERED:  'pill-clay',
    EXPIRED:    'bg-mute/20 text-mute',
    WITHDRAWN:  'bg-mute/20 text-mute',
    CANCELLED:  'bg-mute/20 text-mute',
  };
  return <span className={`pill ${map[state]}`}>{state.toLowerCase()}</span>;
}
